import React, { useState } from "react";
import {
  ShieldCheckIcon,
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Alert from "../components/Alert";
import SecurityNotice from "../components/SecurityNotice";
import ActionButton from "../components/ActionButton";
import SEO from "../components/SEO";
import { useScrollToResults } from "../hooks/useScrollToResults";
import { useCopyFeedback } from "../hooks/useCopyFeedback";
import api from "../utils/api";
import { downloadFile } from "../utils/helpers";
import { generateStandardFilename } from "../../shared/utils/fileHelpers";
// 2026-06-01 : real Top-Level Domain (TLD) validation via tldts,
// shared with the server so the page gives instant feedback that
// matches the server's verdict. See src/shared/utils/domainValidation.js.
import { isRegistrablePublicDomain } from "../../shared/utils/domainValidation";

/**
 * CAA Record Checker Page
 * Checks existing CAA records for a domain via DNS lookup
 */
const CAAChecker = () => {
  // State management
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // 2026-07-04 tools-wide overhaul (operator-approved), audit item
  // #12 : state-driven transient "Copied!" feedback replacing the
  // previous SILENT copies. Now the shared success-gated hook (see
  // src/client/hooks/useCopyFeedback.js) : the status holds at most
  // one key ("all" or "record-<index>") and clears after 2 seconds.
  const { copyStatus, handleCopy, resetCopyStatus } = useCopyFeedback();
  const resultsRef = useScrollToResults(result);

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    // Stale copy feedback goes with the result it belonged to
    // (positional keys would otherwise light up the NEW result's row
    // inside the 2-second window).
    resetCopyStatus();

    // Validate domain
    if (!domain.trim()) {
      setError("A domain name is required.");
      return;
    }
    // Real Top-Level Domain (TLD) check for instant feedback ; the
    // server applies the same gate authoritatively. Strip any "*."
    // wildcard prefix before the realness check.
    if (!isRegistrablePublicDomain(domain.trim().replace(/^\*\./, ""))) {
      setError(
        "The domain name uses an unrecognized or non-public Top-Level Domain (TLD)."
      );
      return;
    }

    setLoading(true);

    try {
      const response = await api.checkCAA(domain.trim());
      setResult(response.data);
    } catch (err) {
      setError(err.message || "The CAA records could not be checked.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle reset button
   */
  const handleReset = () => {
    setDomain("");
    setError(null);
    setResult(null);
    resetCopyStatus();
  };

  /**
   * Strip control characters from a record value before serializing :
   * the DoH parse cannot deliver them (its line regex has no
   * multiline flag), but the native dns.resolveCaa path passes values
   * verbatim, and a newline inside a value would break the
   * one-record-per-line integrity of the copied/downloaded text.
   * Character-code filtering rather than a control-character regex,
   * which the no-control-regex lint rule rejects.
   */
  const stripControlCharacters = (value) =>
    [...value]
      .filter((ch) => {
        const code = ch.charCodeAt(0);
        // C0 controls and DEL, plus the Unicode line/paragraph
        // separators (U+2028/U+2029), which many editors and parsers
        // treat as line breaks - the same one-record-per-line failure
        // mode as a raw newline.
        return (
          code >= 0x20 && code !== 0x7f && code !== 0x2028 && code !== 0x2029
        );
      })
      .join("");

  /**
   * The flags byte to show AND serialize for one record : the raw
   * published value when the response carries it, the
   * critical-derived 128/0 reconstruction otherwise. One producer for
   * the table's Critical column and serializeRecord, so the screen
   * can never display one flags value while the copied/downloaded
   * line carries another (a zone publishing 129 must read "Yes (129)"
   * on screen, not a hard-coded "Yes (128)").
   */
  const flagsByteOf = (record) =>
    Number.isInteger(record.flags) ? record.flags : record.critical ? 128 : 0;

  /**
   * Serialize one found record as a full zone-file line, exactly the
   * shape the CAA Record Generator emits ("host. IN CAA rdata").
   *
   * 2026-08-07 (operator-directed) : the RDATA is flags byte FIRST,
   * then the tag, then the quoted value - the RFC 8659 presentation
   * order (the client previously led with the tag, an order zone-file
   * parsers do not accept). The full zone-line shape exists because
   * the two CAA pages must serialize identically : a bare-RDATA file
   * downloaded here could not be pasted back into a zone while the
   * generator's could, which would defeat the very consistency the
   * Download exists for. Both pages emit the same shape, per-row copy
   * included.
   *
   * Note `record.flag` holds the RFC 8659 TAG string (issue /
   * issuewild / iodef / a preserved unknown tag) : the field name
   * predates this code and is part of the public /api/caa/check
   * response shape, so it is not renamed.
   *
   * Interior quotes and backslashes in the value are escaped so the
   * emitted line always PARSES : RFC 8659 permits both inside the
   * quoted string, and dnsResolver.js:resolveCaa strips only the
   * SURROUNDING quotes, so a raw interior quote would otherwise break
   * the line. Precision on fidelity : the resolver also never
   * UN-escapes, so a zone that published an escaped interior quote
   * arrives here with its backslash still in the value and gets
   * re-escaped (the pasted-back value gains a backslash). Full
   * fidelity for that essentially-unheard-of case needs resolver-side
   * unescaping, not more client escaping ; this line guarantees a
   * parseable file, not byte-fidelity for pre-escaped values.
   *
   * The flags byte is the RAW value the zone published
   * (record.flags, preserved end-to-end by dnsResolver / the
   * caa-checker route) : reducing it to 128/0 from the critical bit
   * would silently paste back a different record for combined values
   * like 129. The critical-derived fallback only covers a response
   * without the flags field (a boolean-critical Node resolver path).
   *
   * The TAG needs no rewriting here : every record that reaches this
   * serializer has passed the isUnparseablePlaceholder gate, whose
   * rollout-window rule excludes any record with a non-conforming tag
   * outright (exclusion beats rewriting : see its comment). String()
   * coercion remains as the same non-throwing stance the value gets.
   */
  const serializeRecord = (record) =>
    `${result.domain}. IN CAA ${flagsByteOf(
      record
    )} ${String(record.flag)} "${stripControlCharacters(
      // Coerced like the tag above : a non-string value (unreachable
      // through today's resolver paths, but the same defense-in-depth
      // stance) must not throw out of the spread inside
      // stripControlCharacters and kill the click with no feedback.
      String(record.value ?? "")
    ).replace(/(["\\])/g, "\\$1")}"`;

  /**
   * routes/caa-checker.js classifies a record it cannot read
   * faithfully with a STRUCTURAL parseable:false marker : no
   * recognizable tag, a tag that is not plain US-ASCII letters/digits
   * (and so cannot be a valid RFC 8659 tag - those two also get the
   * display placeholder texts), or a flags value outside the single
   * octet RFC 8659 allows (the record keeps its real tag and value on
   * screen, but its flags byte cannot be what the zone published, so
   * serializing it would fabricate a record). Gating on the boolean
   * rather than matching the
   * "unknown"/"(unknown)" sentinel strings means an (absurdly
   * unlikely) real record carrying exactly those texts is never
   * wrongly dropped. A zone line built from an unparseable record is
   * garbage a customer might paste back, whether from a file or from
   * the clipboard.
   *
   * The rollout-window rules are SECONDARY, scoped to
   * parseable === undefined : a response from a not-yet-updated
   * instance (multi-instance rollout window) carries no parseable
   * field, so in that window a record is also excluded when it is the
   * old placeholder ("unknown"/"(unknown)") OR its tag is not plain
   * US-ASCII letters/digits - the shape no valid RFC 8659 tag can
   * violate. Exclusion, never rewriting : sanitizing a bad tag into a
   * valid-looking one (issue-wild -> issuewild) would hand the
   * customer a wrong record, the exact silent transformation the
   * iodef e-mail ruling in caaService.js rejects. On any response
   * that DOES carry the field, the structural boolean alone decides,
   * so a real record carrying the placeholder texts is never wrongly
   * dropped there (the server already tag-gates before setting
   * parseable).
   */
  const isUnparseablePlaceholder = (record) =>
    record.parseable === false ||
    (record.parseable === undefined &&
      ((record.flag === "unknown" && record.value === "(unknown)") ||
        !/^[A-Za-z0-9]+$/.test(String(record.flag))));

  /**
   * The records worth serializing anywhere : placeholder rows (see
   * isUnparseablePlaceholder above) are excluded from the download
   * file and the Copy Everything payload, and their table rows carry no
   * row-level copy control either - one rule for every affordance.
   * Real-but-unrecognized tags (e.g. contactemail, RFC 8657) arrive
   * with their actual tag and value preserved and ARE kept : they are
   * legitimate records. The on-screen table still DISPLAYS placeholder
   * rows so the customer can see something unparseable exists.
   */
  const serializableRecords = () =>
    result.records.filter((r) => !isUnparseablePlaceholder(r));

  /**
   * Handle copy single record, keyed by row index (audit item #12) :
   * the shared hook gates the "Copied!" confirmation on the clipboard
   * helper's success flag, so a failed copy never claims otherwise.
   */
  const handleCopyRecord = (record, index) =>
    handleCopy(serializeRecord(record), `record-${index}`);

  /**
   * Handle copy all records (same feedback contract as
   * handleCopyRecord above, keyed "all").
   */
  const handleCopyAll = () => {
    if (!result || result.records.length === 0) return;
    const lines = serializableRecords().map(serializeRecord);
    if (lines.length === 0) return;

    handleCopy(lines.join("\n"), "all");
  };

  /**
   * 2026-08-07 download/copy consistency pass (operator-directed) :
   * every tool whose result is a savable artifact offers a Download
   * button under the standard {identifier}-{type}-{timestamp} filename
   * (docs/FILENAME_STANDARDS.md). The file content is byte-identical to
   * what Copy Everything puts on the clipboard (one zone line per
   * record, see serializeRecord / serializableRecords above), so a
   * customer gets the same records whichever affordance they use.
   */
  const handleDownloadRecords = () => {
    if (!result || result.records.length === 0) return;
    const lines = serializableRecords().map(serializeRecord);
    if (lines.length === 0) return;

    downloadFile(
      lines.join("\n"),
      generateStandardFilename(result.domain, "caa-check", "txt")
    );
  };

  // Computed once per render : the button-pair gate, the
  // unreadable-records note, and its wording all read the same count,
  // so a single value removes both the repeated filter passes and any
  // question of whether the note and the gate saw the same list. The
  // handlers keep calling serializableRecords() directly : they run on
  // click, not per render.
  const serializableCount = result ? serializableRecords().length : 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title="CAA Record Checker - Certificate Authority Authorization Lookup"
        description="Check existing CAA (Certificate Authority Authorization) DNS records for any domain. Verify which Certificate Authorities are authorized to issue SSL Certificates for your domain."
        keywords="CAA checker, CAA lookup, CAA record lookup, DNS CAA, Certificate Authority Authorization, SSL Certificate Authority, check CAA records, verify CAA, domain CAA, CAA DNS lookup"
        canonicalUrl="https://tools.trustico.com/caa-check"
      />

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-heading mb-4 text-center">
          CAA Record Checker
        </h1>
        <p className="text-body">
          Look up the CAA (Certificate Authority Authorization) records
          published for any domain. CAA records let a domain owner state which
          Certificate Authorities are allowed to issue SSL Certificates for that
          domain, and Certificate Authorities are required to check them before
          issuing. Enter a domain to see the records currently published, or to
          confirm that none exist yet.
        </p>
      </div>

      {/* The Security Notice comes FIRST, directly under the h1, as it
          does on every other page. Placed after the About card it read
          h1 -> h3 -> h2, which is both a heading skip and this page
          alone putting its notice somewhere different. */}
      <SecurityNotice />

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-heading mb-2">
            Domain Name
          </h2>
          <p className="text-body mb-3">
            Enter the domain name for which you want to check CAA records. CAA
            records can be set at any level of your domain hierarchy.
          </p>
          <p className="text-body mb-4">
            Do not include http:// or https:// protocols. Enter only the domain
            name such as example.com or subdomain.example.com.
          </p>
          {/* 2026-07-04 operator consistency directive : the HTML
              `required` attribute was dropped because the disabled gate
              on the submit button already blocks empty click-submits
              and the handleSubmit trim guard covers direct form
              submission (the reference page, CSRGenerator.jsx, carries
              no `required` attributes). */}
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="example.com"
            className="input"
          />
        </div>

        <div className="flex justify-center">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
            {/* 2026-07-04 operator consistency directive : the primary
                action is disabled until the required input is non-empty
                (the ChainAnalyzer / CertificateMatcher gate pattern) ;
                the handleSubmit guard covers direct form submission. */}
            <ActionButton
              type="submit"
              variant="primary"
              disabled={loading || !domain.trim()}
              loading={loading}
              loadingText="Checking..."
              icon={ShieldCheckIcon}
            >
              Check CAA Records
            </ActionButton>
            <ActionButton
              type="button"
              onClick={handleReset}
              variant="outline"
              icon={XMarkIcon}
            >
              {/* 2026-07-04 operator consistency directive : reset
                  buttons across every tool page say "Reset" (the
                  previous label was "Clear Data"). */}
              Reset
            </ActionButton>
          </div>
        </div>
      </form>

      {/* 2026-07-04 tools-wide overhaul (operator-approved), audit
          Tier 3 loading-idiom item : the separate LoadingSpinner card
          that used to render here was removed. The inline
          ActionButton loading / loadingText="Checking..." state above
          is the house loading idiom (CSRGenerator and the Issuance
          feature are the reference pattern). */}

      {error && (
        <Alert type="error" className="mt-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* No !loading guard needed : handleSubmit nulls result before
          every request, so result and loading are never truthy
          together (the guard only existed for the removed spinner
          card). */}
      {result && (
        <div ref={resultsRef} className="mt-8 animate-fade-in">
          <div className="card p-6">
            <div className="flex justify-between items-center mb-2">
              {/* 2026-07-04 operator consistency directive : recased
                  from h2 text-2xl font-bold to the reference section
                  heading size (text-lg font-semibold) - the h1 page
                  title is the only larger heading a tool page carries. */}
              <h2 className="text-lg font-semibold text-heading">
                CAA Records for {result.domain}
              </h2>
              {result.hasRecords && serializableCount > 0 && (
                /* 2026-08-07 download/copy consistency pass
                   (operator-directed) : Copy Everything gains a Download
                   sibling, grouped in the flex gap-3 header pair the
                   PasswordGenerator results header established. The
                   gate is the SERIALIZABLE count, not hasRecords : a
                   result whose records are all unparseable
                   placeholders would otherwise render two buttons
                   that silently do nothing (the empty-lines early
                   returns in the handlers are the defensive backstop,
                   this gate is the customer-facing rule). */
                <div className="flex flex-wrap gap-3">
                  <ActionButton
                    variant="outline"
                    onClick={handleCopyAll}
                    icon={ClipboardDocumentIcon}
                  >
                    {/* Transient label swap driven by copyStatus (audit
                        item #12) - same idiom as the "Copy CSR" button
                        in CSRGenerator.jsx. */}
                    {copyStatus.all ? "Copied!" : "Copy Everything"}
                  </ActionButton>
                  <ActionButton
                    variant="outline"
                    onClick={handleDownloadRecords}
                    icon={ArrowDownTrayIcon}
                  >
                    Download
                  </ActionButton>
                </div>
              )}
            </div>
            {/* 2026-07-04 operator consistency directive : every
                section heading carries an explanatory paragraph (the
                CSRGenerator.jsx reference idiom) ; the result-section
                headings on this page previously had none. */}
            <p className="text-body mb-4">
              These are the CAA records currently published in DNS for this
              domain, with a summary of how many records were found and which
              Certificate Authorities they authorize.
            </p>

            {/* Summary Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                <span className="text-xs font-medium text-muted uppercase tracking-wider">
                  Status
                </span>
                <p className="text-heading mt-1">
                  {result.hasRecords ? (
                    <span className="text-green-700 dark:text-green-400">
                      Found
                    </span>
                  ) : (
                    <span className="text-yellow-700 dark:text-yellow-400">
                      No Records
                    </span>
                  )}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                <span className="text-xs font-medium text-muted uppercase tracking-wider">
                  Total Records
                </span>
                <p className="text-heading mt-1">{result.recordCount}</p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                <span className="text-xs font-medium text-muted uppercase tracking-wider">
                  Authorized CAs
                </span>
                <p className="text-heading mt-1">
                  {result.summary.authorizedCAs.length}
                </p>
              </div>
            </div>

            {/* No Records Message */}
            {!result.hasRecords && (
              <Alert type="info" className="mt-4">
                <strong>No CAA Records Found :</strong> This domain does not
                have any CAA records configured in DNS. This means any
                Certificate Authority can issue SSL Certificates for this
                domain. Consider using the{" "}
                <a href="/caa" className="text-link hover:text-link-hover">
                  CAA Record Generator
                </a>{" "}
                to create protective CAA records.
              </Alert>
            )}

            {/* All Issuance Blocked Warning */}
            {result.summary.blocksAllIssuance && (
              <Alert type="warning" className="mt-4">
                <strong>All Issuance Blocked :</strong> This domain has CAA
                records that prevent ALL Certificate Authorities from issuing
                SSL Certificates. No CA can issue Certificates for this domain.
              </Alert>
            )}
          </div>

          {/* CAA Records Table */}
          {result.hasRecords && (
            <div className="card p-6 mt-6">
              <h2 className="text-lg font-semibold text-heading mb-2">
                DNS CAA Records ({result.recordCount})
              </h2>
              {/* Explanatory paragraph per the 2026-07-04 operator
                  consistency directive (see the results card above). */}
              <p className="text-body mb-4">
                Each row below is one CAA record exactly as it appears in DNS,
                with its record type, value, and critical flag.
              </p>

              {/* The exclusion is never silent : when unreadable rows
                  exist, the customer is told they are shown here but
                  left out of Copy Everything and the downloaded file (the
                  aggregate payloads exclude them via
                  serializableRecords, and a file quietly missing rows
                  the summary counts would read as complete). */}
              {serializableCount < result.records.length && (
                <Alert type="warning" className="mb-4">
                  {result.records.length - serializableCount} of the{" "}
                  {result.records.length} records found could not be read as a
                  valid CAA record.{" "}
                  {/* The second sentence matches what is actually on
                      screen : with zero readable records the Copy Everything /
                      Download pair is hidden, so pointing the customer
                      at those controls would reference buttons that do
                      not exist. */}
                  {serializableCount > 0
                    ? "Unreadable records are shown below but are not included when you copy all records or download the file."
                    : "Unreadable records are shown below, and there is nothing available to copy or download."}
                </Alert>
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                        Flag
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                        Value
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                        Critical
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {result.records.map((record, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              record.flag === "issue"
                                ? "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200"
                                : record.flag === "issuewild"
                                  ? "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200"
                                  : record.flag === "iodef"
                                    ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200"
                                    : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                            }`}
                          >
                            {record.flag}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-mono text-heading break-all">
                            {record.value}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {/* The parenthetical is the SAME flags byte
                              the copy/download line carries
                              (flagsByteOf), never a hard-coded
                              128/0 : the screen and the file must
                              agree on combined values like 129. */}
                          {record.critical ? (
                            <span className="text-red-600 dark:text-red-300 font-medium">
                              Yes ({flagsByteOf(record)})
                            </span>
                          ) : (
                            <span className="text-muted">
                              No ({flagsByteOf(record)})
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {/* Per-row transient "Copied!" swap keyed by
                              row index (audit item #12). Green while
                              confirmed so the state change is visible
                              on a text-only control. */}
                          {/* aria-label distinguishes the otherwise
                              identically-named row copies for screen
                              readers ; the chain diagram's
                              per-certificate aria-label is the house
                              precedent. Placeholder rows carry no
                              copy control at all : the same
                              unparseable line the file excludes must
                              not reach the clipboard either (one
                              rule, every affordance - see
                              serializableRecords above). */}
                          {!isUnparseablePlaceholder(record) && (
                            <button
                              type="button"
                              onClick={() => handleCopyRecord(record, index)}
                              aria-label={
                                /* Flips with the visible state : a
                                   static aria-label would mask the
                                   "Copied!" confirmation from screen
                                   readers (see the CopyValueButton
                                   JSDoc). */
                                copyStatus[`record-${index}`]
                                  ? "Copied!"
                                  : `Copy Record ${index + 1}`
                              }
                              className={
                                copyStatus[`record-${index}`]
                                  ? "font-medium text-green-700 dark:text-green-400"
                                  : "text-link hover:text-link-hover"
                              }
                            >
                              {copyStatus[`record-${index}`]
                                ? "Copied!"
                                : "Copy"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Authorized CAs */}
          {result.summary.authorizedCAs.length > 0 && (
            <div className="card p-6 mt-6">
              <h2 className="text-lg font-semibold text-heading mb-2">
                Authorized Certificate Authorities
              </h2>
              {/* Explanatory paragraph per the 2026-07-04 operator
                  consistency directive (see the results card above). */}
              <p className="text-body mb-4">
                Only the Certificate Authorities listed below are authorized to
                issue SSL Certificates for this domain.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {result.summary.authorizedCAs.map((ca, index) => (
                  <div
                    key={index}
                    className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg border border-blue-200 dark:border-blue-800/50"
                  >
                    <p className="font-mono text-heading break-all">
                      {ca || "(Blocks all issuance)"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Record Type Summary */}
          {result.hasRecords && (
            <div className="card p-6 mt-6">
              <h2 className="text-lg font-semibold text-heading mb-2">
                Record Type Summary
              </h2>
              {/* Explanatory paragraph per the 2026-07-04 operator
                  consistency directive (see the results card above). */}
              <p className="text-body mb-4">
                This summary counts the CAA records found for each record type.
              </p>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <span className="font-medium text-heading">
                    Issue Records (Standard Certificates)
                  </span>
                  <span className="font-bold text-link">
                    {result.summary.issueCount}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <span className="font-medium text-heading">
                    Issuewild Records (Wildcard Certificates)
                  </span>
                  <span className="font-bold text-purple-600 dark:text-purple-400">
                    {result.summary.issuewildCount}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <span className="font-medium text-heading">
                    IODEF Records (Violation Reports)
                  </span>
                  <span className="font-bold text-green-700 dark:text-green-400">
                    {result.summary.iodefCount}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Help & Information */}
          <div className="card p-6 mt-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              Understanding CAA Records
            </h2>
            {/* Explanatory paragraph per the 2026-07-04 operator
                consistency directive (see the results card above). */}
            <p className="text-body mb-4">
              The definitions below explain what each CAA record type means for
              SSL Certificate issuance on your domain.
            </p>
            <div className="space-y-4 text-body">
              <div>
                <strong className="text-heading">Issue :</strong> Specifies
                which CAs are authorized to issue standard SSL Certificates for
                this domain.
              </div>
              <div>
                <strong className="text-heading">Issuewild :</strong> Specifies
                which CAs are authorized to issue wildcard SSL Certificates
                (*.example.com) for this domain.
              </div>
              <div>
                <strong className="text-heading">IODEF :</strong> Specifies an
                e-mail address or URL where CAs should report policy violations.
              </div>
              <div>
                <strong className="text-heading">Critical Flag :</strong> If set
                (128), the CA must understand and process the record. If not set
                (0), the CA can ignore unknown record types.
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p>
                  <strong className="text-heading">Tip :</strong> Use the{" "}
                  <a href="/caa" className="text-link hover:text-link-hover">
                    CAA Record Generator
                  </a>{" "}
                  to create new CAA records for your domain if none exist.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CAAChecker;
